import { Globe, Users, Lock, X, Database, Image, Type, Settings2 } from "lucide-react";
import { cn } from "../../lib/utils";
import type { MapAssetDependenciesSheetProps, MapWorkspaceShareScope } from "../../contracts/map-workspace";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../internal/primitives/select";
import { Separator } from "../../internal/primitives/separator";

const ShareScopeIcon = ({ scope }: { scope?: MapWorkspaceShareScope | null }) => {
  switch (scope) {
    case "public":
      return <Globe className="h-3 w-3" />;
    case "shared":
      return <Users className="h-3 w-3" />;
    case "tenant":
      return <Lock className="h-3 w-3" />;
    default:
      return null;
  }
}

export function MapAssetDependenciesSheet({
  assetKind,
  basemap,
  mountedSources,
  spriteOptions,
  glyphOptions,
  onRemoveMountedSource,
  onSelectSprite,
  onClearSprite,
  onSelectGlyph,
  onClearGlyph,
  className,
}: MapAssetDependenciesSheetProps) {
  const assetLabel = assetKind === "style" ? "Style" : "Custom Map";

  return (
    <div className={cn("flex flex-col h-full bg-background text-foreground", className)}>
      <div className="flex-1 overflow-y-auto p-4 space-y-8">
        {/* Basemap Section */}
        <section className="space-y-3">
          <div className="flex items-center justify-between gap-3 text-muted-foreground">
            <div className="flex items-center gap-2">
              <Settings2 className="h-4 w-4" />
              <h3 className="text-xs font-semibold uppercase tracking-wider">Basemap Runtime</h3>
            </div>
            <span className="rounded-full border border-border bg-muted px-2 py-0.5 text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">
              {assetLabel}
            </span>
          </div>
          <div className="rounded-lg border bg-card p-4 space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium">Theme</span>
              <span className="text-sm text-muted-foreground">{basemap.themeLabel}</span>
            </div>
            <div className="space-y-1">
              <span className="text-[10px] text-muted-foreground uppercase tracking-tight">Sprite Set</span>
              <p className="text-xs font-mono bg-muted/50 p-1.5 rounded truncate" title={basemap.spriteUrl}>
                {basemap.spriteUrl}
              </p>
            </div>
            <div className="space-y-1">
              <span className="text-[10px] text-muted-foreground uppercase tracking-tight">Glyph Template</span>
              <p className="text-xs font-mono bg-muted/50 p-1.5 rounded truncate" title={basemap.glyphTemplateUrl}>
                {basemap.glyphTemplateUrl}
              </p>
            </div>
          </div>
        </section>

        {/* Mounted Sources Section */}
        <section className="space-y-3">
          <div className="flex items-center gap-2 text-muted-foreground">
            <Database className="h-4 w-4" />
            <h3 className="text-xs font-semibold uppercase tracking-wider">Mounted Sources</h3>
          </div>
          <div className="space-y-2">
            {mountedSources.length === 0 ? (
              <p className="text-sm text-muted-foreground italic bg-muted/20 p-4 rounded-lg border border-dashed text-center">
                No sources mounted.
              </p>
            ) : (
              mountedSources.map((source) => (
                <div key={source.id} className="flex items-center justify-between gap-3 p-3 rounded-lg border bg-card">
                  <div className="flex flex-col gap-0.5 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium truncate">{source.label}</span>
                      <span className="text-[10px] text-muted-foreground font-mono bg-muted px-1 rounded">
                        {source.alias}
                      </span>
                    </div>
                    <div className="flex items-center gap-1.5 text-[10px] text-muted-foreground uppercase tracking-wider">
                      <ShareScopeIcon scope={source.shareScope} />
                      {source.shareScope}
                    </div>
                  </div>
                  <button
                    onClick={() => onRemoveMountedSource?.(source.id)}
                    className="p-1.5 rounded-md hover:bg-destructive/10 hover:text-destructive text-muted-foreground transition-colors"
                    title="Remove source"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              ))
            )}
          </div>
        </section>

        <Separator />

        {/* Asset Overrides Section */}
        <section className="space-y-6">
          {/* Sprite Selection */}
          <div className="space-y-3">
            <div className="flex items-center gap-2 text-muted-foreground">
              <Image className="h-4 w-4" />
              <h3 className="text-xs font-semibold uppercase tracking-wider">Style Sprites</h3>
            </div>
            <div className="space-y-1.5">
              <Select
                value={spriteOptions.find((option) => option.selected)?.id || "none"}
                onValueChange={(value) => {
                  if (value === "none") onClearSprite?.();
                  else onSelectSprite?.(value);
                }}
              >
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Select sprite set" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None (Use Basemap)</SelectItem>
                  {spriteOptions.map((option) => (
                    <SelectItem key={option.id} value={option.id} disabled={option.disabled}>
                      <div className="flex flex-col items-start text-left">
                        <span className="font-medium text-sm">{option.label}</span>
                        {option.description ? (
                          <span className="text-[10px] text-muted-foreground line-clamp-1">{option.description}</span>
                        ) : null}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-[10px] text-muted-foreground">
                Overrides the default sprite sheet for icons and patterns.
              </p>
            </div>
          </div>

          {/* Glyph Selection */}
          <div className="space-y-3">
            <div className="flex items-center gap-2 text-muted-foreground">
              <Type className="h-4 w-4" />
              <h3 className="text-xs font-semibold uppercase tracking-wider">Style Glyphs</h3>
            </div>
            <div className="space-y-1.5">
              <Select
                value={glyphOptions.find((option) => option.selected)?.id || "none"}
                onValueChange={(value) => {
                  if (value === "none") onClearGlyph?.();
                  else onSelectGlyph?.(value);
                }}
              >
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Select glyph set" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None (Use Basemap)</SelectItem>
                  {glyphOptions.map((option) => (
                    <SelectItem key={option.id} value={option.id} disabled={option.disabled}>
                      <div className="flex flex-col items-start text-left">
                        <span className="font-medium text-sm">{option.label}</span>
                        {option.description ? (
                          <span className="text-[10px] text-muted-foreground line-clamp-1">{option.description}</span>
                        ) : null}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-[10px] text-muted-foreground">
                Overrides the default font stacks for labels and text symbols.
              </p>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}
